// M1 throwaway: FE-owned chat lifecycle for flow (Live) analytics.
//
// The per-conversation context (workspace schema snapshot, flow versions) is
// assembled here; the prompt and tools come from the backend. The shared session
// state machine is in useAnalyticsChatSession.

import { useCallback } from "react";

import {
  type AnalyticsChatResult,
  useAnalyticsChatSession,
} from "src/analytics/copilot/useAnalyticsChatSession";
import { useModules } from "src/api/modulesApi";
import { useCurrentWorkspace } from "src/api/queries";
import { ModuleKind } from "src/clients/flow-api";
import {
  LIVE_ANALYTICS_VIRTUAL_AGENT_ID,
  LIVE_ANALYTICS_VIRTUAL_VERSION_ID,
} from "src/flow/analytics/m1/liveAnalyticsVirtualAgent";
import { useLiveAnalyticsPreflight } from "src/flow/analytics/m1/useLiveAnalyticsPreflight";
import { useEnvironment } from "src/hooks/useEnvironment";

// Surface expiry on the FE a day before the backend hard limit so the user gets
// a graceful "start a new chat" message instead of a failed invoke at the edge.
const SESSION_EXPIRY_THRESHOLD_MS = 6 * 24 * 60 * 60 * 1000;
const SESSION_EXPIRED_MESSAGE =
  "This chat has expired. Start a new conversation to continue.";

const SESSIONS_STORAGE_KEY = "liveAnalyticsM1SessionsV2";

export const useLiveAnalyticsChat = ({
  flowId,
  flowName,
}: {
  flowId: string;
  flowName: string;
}): AnalyticsChatResult => {
  const { data: workspace } = useCurrentWorkspace();
  const [environment] = useEnvironment();

  const { data: analyticsModules } = useModules({
    workspaceId: workspace?.id,
    kind: ModuleKind.ANALYTICS,
    enabled: Boolean(workspace?.id),
  });
  const demoMode =
    analyticsModules?.[0]?.spec?.config?.live_analytics_demo_mode === true;

  // Nothing to provision: the virtual agent needs no agent or version record,
  // so this resolves without a round trip and the first message goes straight
  // to invoke.
  const provision = useCallback(
    () => Promise.resolve(LIVE_ANALYTICS_VIRTUAL_AGENT_ID),
    [],
  );

  // The schema snapshot and version list travel as structured_input on the first
  // message of a conversation, so they're scoped to the flow rather than to an
  // agent: one fetch per flow, with the query owning the caching and the retries.
  const { structuredInput: initialStructuredInput, flowVersionsResolved } =
    useLiveAnalyticsPreflight({ flowId, flowName });

  return useAnalyticsChatSession({
    storageKey: SESSIONS_STORAGE_KEY,
    sessionSlotKey: flowId,
    environment,
    provision,
    agentVersionId: LIVE_ANALYTICS_VIRTUAL_VERSION_ID,
    provisionReady: flowVersionsResolved,
    // `null` until the snapshot resolves; the hook buffers sends until then.
    initialStructuredInput,
    sessionExpiryThresholdMs: SESSION_EXPIRY_THRESHOLD_MS,
    sessionExpiredMessage: SESSION_EXPIRED_MESSAGE,
    injectCurrentDate: !demoMode,
  });
};
